import { api } from './client';

export interface EmployeeOrganizationRef {
  id: string;
  shortName: string;
}

export interface Employee {
  id: string;
  organizationId: string;
  organization: EmployeeOrganizationRef;
  lastName: string;
  firstName: string;
  middleName: string | null;
  fullName: string;
  position: string | null;
  personnelNumber: string | null;
  passportSeries: string | null;
  passportNumber: string | null;
  passportIssuedBy: string | null;
  passportIssueDate: string | null;
  passportCode: string | null;
  driverLicense: string | null;
  driverLicenseSeries: string | null;
  driverLicenseNumber: string | null;
  driverLicenseIssuedBy: string | null;
  driverLicenseIssueDate: string | null;
  isDriver: boolean;
  phone: string | null;
  inn: string | null;
  isActive: boolean;
}

export interface EmployeeInput {
  organizationId: string;
  lastName: string;
  firstName: string;
  middleName?: string | null;
  position?: string | null;
  personnelNumber?: string | null;
  passportSeries?: string | null;
  passportNumber?: string | null;
  passportIssuedBy?: string | null;
  passportIssueDate?: string | null;
  passportCode?: string | null;
  driverLicense?: string | null;
  driverLicenseSeries?: string | null;
  driverLicenseNumber?: string | null;
  driverLicenseIssuedBy?: string | null;
  driverLicenseIssueDate?: string | null;
  isDriver?: boolean;
  phone?: string | null;
  inn?: string | null;
}

export interface EmployeeListResponse {
  items: Employee[];
  total: number;
  page: number;
  pageSize: number;
}

export interface EmployeeListParams {
  search?: string;
  organizationId?: string;
  onlyDrivers?: boolean;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

/** Паспорт заполнен полностью — без этого доверенность не выписать. */
export function hasPassportData(employee: Employee): boolean {
  return !!(
    employee.passportSeries &&
    employee.passportNumber &&
    employee.passportIssuedBy &&
    employee.passportIssueDate
  );
}

/** Водительское удостоверение заполнено полностью. */
export function hasDriverLicenseData(employee: Employee): boolean {
  return !!(
    employee.driverLicenseSeries &&
    employee.driverLicenseNumber &&
    employee.driverLicenseIssuedBy &&
    employee.driverLicenseIssueDate
  );
}

/** «Иванов И. И.» — как принято в шапке печатных форм. */
export function shortName(employee: Pick<Employee, 'lastName' | 'firstName' | 'middleName'>): string {
  const initials = [employee.firstName, employee.middleName]
    .filter(Boolean)
    .map((part) => `${part!.charAt(0).toUpperCase()}.`)
    .join(' ');
  return initials ? `${employee.lastName} ${initials}` : employee.lastName;
}

function buildQuery(params: EmployeeListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.onlyDrivers) search.set('onlyDrivers', 'true');
  if (params.includeInactive) search.set('includeInactive', 'true');
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const employeesApi = {
  list: (params: EmployeeListParams = {}) => api.get<EmployeeListResponse>(`/employees${buildQuery(params)}`),

  get: (id: string) => api.get<{ employee: Employee }>(`/employees/${id}`),

  create: (data: EmployeeInput) => api.post<{ employee: Employee }>('/employees', data),

  update: (id: string, data: Partial<EmployeeInput>) =>
    api.patch<{ employee: Employee }>(`/employees/${id}`, data),

  remove: (id: string) => api.delete<void>(`/employees/${id}`),

  activate: (id: string) => api.post<{ employee: Employee }>(`/employees/${id}/activate`),

  deactivate: (id: string) => api.post<{ employee: Employee }>(`/employees/${id}/deactivate`),
};
